import { forwardRef } from 'react';

import { Icon, Menu, TextButton } from '@zillow/constellation-10';
import { IconClockFilled, IconExternalFilled } from '@zillow/constellation-icons';

import { LoadingButton } from '@zg-rentals/loading-components';

import PropertyConstants from '../../../../constants/PropertyConstants';
import StringConstants from '../../../../constants/StringConstants';

import type { Ref } from 'react';
import type { TextButtonPropsInterface } from '@zillow/constellation-10';
import type { UserPropertySummaryListingSyndicationWebDto } from '@zg-rentals/ts-rental-manager-api-client';

export const DISPLAY_TYPE = {
  DROPDOWN_BUTTON: 'DROPDOWN_BUTTON',
  TEXT_BUTTON: 'TEXT_BUTTON',
} as const;

const origSyndicationByBrandSF = [
  StringConstants.UNFORMATTED_BRAND_NAMES.zillow,
  StringConstants.UNFORMATTED_BRAND_NAMES.trulia,
  StringConstants.UNFORMATTED_BRAND_NAMES.hotpads,
] as const;

const externalSyndicationBrandMF_MAP: Record<string, string> = {
  [StringConstants.UNFORMATTED_BRAND_NAMES.realtor]: StringConstants.UNFORMATTED_BRAND_NAMES.realtor,
  [StringConstants.UNFORMATTED_BRAND_NAMES.rent]: StringConstants.UNFORMATTED_BRAND_NAMES.rent,
  [StringConstants.UNFORMATTED_BRAND_NAMES.redfin]: StringConstants.UNFORMATTED_BRAND_NAMES.redfin,
  [StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide]: StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide,
};

const origSyndicationByBrandMF = [
  StringConstants.UNFORMATTED_BRAND_NAMES.zillow,
  StringConstants.UNFORMATTED_BRAND_NAMES.trulia,
  StringConstants.UNFORMATTED_BRAND_NAMES.hotpads,
  StringConstants.UNFORMATTED_BRAND_NAMES.realtor,
  StringConstants.UNFORMATTED_BRAND_NAMES.rent,
  StringConstants.UNFORMATTED_BRAND_NAMES.redfin,
  StringConstants.UNFORMATTED_BRAND_NAMES.apartmentGuide,
] as const;

type Props = {
  label: string | JSX.Element | null;
  syndication?: UserPropertySummaryListingSyndicationWebDto;
  isMultifamily: boolean;
  displayType?: string;
  useFixedWidth?: boolean;
  size?: TextButtonPropsInterface['size'];
  status?: string;
  onMenuOpen?: () => void;
  isLoading?: boolean;
};

const NetworkView = forwardRef((props: Props, ref) => {
  const {
    label,
    syndication,
    isMultifamily = false,
    displayType = DISPLAY_TYPE.TEXT_BUTTON,
    useFixedWidth = false,
    size = 'lg',
    status = null,
    onMenuOpen = null,
    isLoading = false,
  } = props;

  const syndicationByBrand = isMultifamily ? origSyndicationByBrandMF : origSyndicationByBrandSF;

  const handleMenuOpen = () => {
    if (onMenuOpen) {
      onMenuOpen();
    }
  };

  return (
    <Menu.Root onOpenChange={(open) => open && handleMenuOpen()}>
      <Menu.Trigger>
        {displayType === DISPLAY_TYPE.DROPDOWN_BUTTON ? (
          isLoading ? (
            <LoadingButton isLoading={isLoading} loadingWidth={size === 'sm' ? '50%' : '100%'}>
              &nbsp;
            </LoadingButton>
          ) : (
            <Menu.Button
              data-testid="ViewYourListingDropdownButton"
              size={size}
              title={label as string}
              css={{
                width: useFixedWidth ? { lg: '300px' } : '100%',
              }}
            >
              {label}
            </Menu.Button>
          )
        ) : (
          <TextButton.Root data-testid="NetworkView-TextButton" title={label as string}>
            <TextButton.Label
              css={{
                textStyle: { base: 'body-xs', lg: 'body-sm-bold', xl: 'body-bold' },
              }}
            >
              {label}
            </TextButton.Label>
          </TextButton.Root>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content data-testid="NetworkView" ref={ref as Ref<HTMLDivElement>}>
          {displayType === DISPLAY_TYPE.DROPDOWN_BUTTON &&
            status?.toLowerCase() === PropertyConstants.SYNDICATION_OVERVIEW_STATUS.PUBLISHING && (
              <Menu.Heading level={3}>
                <Icon render={<IconClockFilled />} /> It may take up to 1 hour for your listing to post
              </Menu.Heading>
            )}

          {syndicationByBrand.map((brand) => {
            const displayBrand = StringConstants.FORMATTED_BRAND_NAMES[brand];

            if (syndication?.[brand]?.url) {
              return (
                <Menu.Item key={`menu-item-${brand}`} asChild>
                  <a target="_blank" rel="noopener noreferrer" href={syndication[brand].url}>
                    <Menu.ItemIcon render={<IconExternalFilled />} />
                    <Menu.ItemLabel>{`View on ${displayBrand}`}</Menu.ItemLabel>
                  </a>
                </Menu.Item>
              );
            }

            if (externalSyndicationBrandMF_MAP[brand]) {
              return null;
            }

            return (
              <Menu.Item disabled={true} key={`menu-item-${brand}`}>
                <Menu.ItemIcon render={<IconExternalFilled />} />
                <Menu.ItemLabel>{`View on ${displayBrand}`}</Menu.ItemLabel>
              </Menu.Item>
            );
          })}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
});

NetworkView.displayName = 'NetworkView';

export default NetworkView;
